/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  Platform,
  Alert,
  StyleSheet,
  ScrollView,
  Animated,
  Image,
  Dimensions,
} from "react-native";
import { useTheme } from "../theme/useTheme";
import { useToast } from "../components/LhToastContainer";
import LhButton from "../components/LhButton";
import ReviewAddModal from "../components/qualityAudit/ReviewAddModal";
import { createApiClient, apiClient } from "../api/apiClient";
import { handleQualityAuditErrorWithFriendlyMessage } from "../store/utils/qualityAuditErrorHandler";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import {
  setSelectedDates,
  setReviewAuditData,
  resetAllReviewAuditData,
} from "../store/slices/qualityAuditSlice";
import { useSelectedLocation } from "../hooks";
const RubicMetricImg = require("../assets/rubic_metric.jpeg");

// This is a placeholder for the QualityAuditRedirect screen.
// Move the dynamic observation rendering logic from QualityAudit.tsx here.

// Add prop types for all props to avoid implicit any errors
interface QualityAuditRedirectProps {
  randomObservations: any[];
  currentObservationIndex: number;
  observationDetails: any;
  isRandomObsLoading: boolean;
  randomObsError: string | null;
  addButtonEnabled: boolean;
  handleOpenReviewOverlay: () => void;
  handleCloseReviewOverlay: () => void;
  showReviewOverlay: boolean;
  reviewType: any;
  setReviewType: (val: any) => void;
  reviewStatus: any[];
  setReviewStatus: (val: any) => void;
  reviewComment: string;
  setReviewComment: (val: string) => void;
  handleSubmitReview: (
    status: string,
    formData?: { reviewType: any; reviewStatus: any[]; reviewComment: string }
  ) => void;
  showAddOverlay: boolean;
  handleOpenAddOverlay: () => void;
  handleCloseAddOverlay: () => void;
  submissionTypeOptions: any[];
  submissionDateOptions: any[];
  handleAddRandomObservation: () => void;
  auditFilters: any;
  userEmail: string;
  setAuditFilters: (val: any) => void;
  setSubmissionTypeOptions: (val: any) => void;
  setSubmissionDateOptions: (val: any) => void;
  isSubmittingReview: boolean;
  sparcWalkReady: boolean;
  showSparcWalkLoader: boolean;
  currentSparcWalkObs: any;
  setRandomObservations: (val: any) => void;
  setCurrentObservationIndex: (val: any) => void;
  setObservationDetails: (val: any) => void;
  setShowSparcWalkLoader: (val: any) => void;
  setSparcWalkReady: (val: any) => void;
  setCurrentSparcWalkObs: (val: any) => void;
  // fetchSingleRandomObservation: () => void;
  onRedirectComplete: () => void;
  addOverlayError?: string | null;
  handleRedirectBackPress: () => void;
  isFromBeginButton?: boolean; // Add flag to track if user came from Begin button
}

const QualityAuditRedirect = (props: QualityAuditRedirectProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const {
    randomObservations,
    currentObservationIndex,
    observationDetails,
    isRandomObsLoading,
    randomObsError,
    addButtonEnabled,
    handleOpenReviewOverlay,
    handleCloseReviewOverlay,
    reviewType,
    setReviewType,
    reviewStatus,
    setReviewStatus,
    reviewComment,
    setReviewComment,
    handleSubmitReview,
    handleOpenAddOverlay,
    handleCloseAddOverlay,
    submissionTypeOptions,
    submissionDateOptions,
    handleAddRandomObservation,
    auditFilters,
    userEmail,
    setAuditFilters,
    setSubmissionTypeOptions,
    setSubmissionDateOptions,
    isSubmittingReview,
    sparcWalkReady,
    showSparcWalkLoader,
    currentSparcWalkObs,
    setRandomObservations,
    setCurrentObservationIndex,
    setObservationDetails,
    setShowSparcWalkLoader,
    setSparcWalkReady,
    setCurrentSparcWalkObs,
    // fetchSingleRandomObservation,
    onRedirectComplete,
    addOverlayError, // Accept addOverlayError as a prop
    isFromBeginButton = false, // Extract the new prop with default value
  } = props;

  const dispatch = useDispatch();

  // Scroll-to-top functionality
  const [showScrollToTop, setShowScrollToTop] = React.useState(false);
  const scrollButtonOpacity = React.useRef(new Animated.Value(0)).current;
  const scrollViewRef = React.useRef<ScrollView>(null);

  // Track reviewed observations
  const [reviewedObservations, setReviewedObservations] = React.useState<
    Set<number>
  >(new Set());
  const [isReviewAuditComplete, setIsReviewAuditComplete] =
    React.useState(false);
  // Track which observations have been completed
  const [completedObservations, setCompletedObservations] = React.useState<
    Set<number>
  >(new Set());
  const selectedLocation = useSelectedLocation();

  // Redirect to QualityAudit dashboard when location changes while on this screen
  const initialLocationIdRef = React.useRef<string | undefined>(selectedLocation?.id);
  const handleRedirectBackPressRef = React.useRef(props.handleRedirectBackPress);
  React.useEffect(() => { handleRedirectBackPressRef.current = props.handleRedirectBackPress; }, [props.handleRedirectBackPress]);
  const hasRedirectedRef = React.useRef(false);
  React.useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      handleRedirectBackPressRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  // Reset reviewed set when adding new observations
  const modifiedBy = useSelector(
    (state: RootState) => state.qualityAudit.modifiedBy
  );
  const createdBy = useSelector(
    (state: RootState) => state.qualityAudit.createdBy
  );

  // Get the selectedRowData from Redux to check the status
  const selectedRowData = useSelector(
    (state: RootState) => state.qualityAudit.selectedRowData
  );
  const isStatusCompleted =
    selectedRowData?.status?.toLowerCase() === "completed";

  // Get audit completion status from Redux
  const auditCompletionStatus = useSelector(
    (state: RootState) => state.qualityAudit.auditCompletionStatus
  );

  // Get stored review audit data from Redux
  const reviewAuditData = useSelector(
    (state: RootState) => state.qualityAudit.reviewAuditData
  );

  // Get current observation's stored review data
  const currentObservationId = React.useMemo(() => {
    return randomObservations.length > 0 &&
      currentObservationIndex < randomObservations.length
      ? randomObservations[currentObservationIndex]?.observation_id?.toString()
      : null;
  }, [randomObservations, currentObservationIndex]);

  const storedReviewData = React.useMemo(() => {
    return currentObservationId ? reviewAuditData[currentObservationId] : null;
  }, [currentObservationId, reviewAuditData]);

  // Debug logging for observation ID and stored data
  React.useEffect(() => {
    console.log("Current observation ID:", currentObservationId);
    console.log("All review audit data in Redux:", reviewAuditData);
    console.log(
      "Stored review data for current observation:",
      storedReviewData
    );
  }, [currentObservationId, reviewAuditData, storedReviewData]);

  // Calculate if Review and Save for later buttons should be enabled
  const currentAuditId = selectedRowData?.id;
  const completionInfo = currentAuditId
    ? auditCompletionStatus[currentAuditId]
    : null;
  const buttonsEnabled = completionInfo ? !completionInfo.isCompleted : true; // Enable if not completed or no info available

  console.log("Current audit ID:", currentAuditId);
  console.log("Completion info:", completionInfo);
  console.log("Buttons enabled:", buttonsEnabled);

  // Add validation state for Review overlay
  const [showReviewValidation, setShowReviewValidation] = React.useState(false);

  // Track if current observation has been reviewed/completed
  const [currentObservationReviewed, setCurrentObservationReviewed] =
    React.useState(false);
  const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  console.log("FieldUsers from Redux:", FieldUsers);
  const COEUsers = useSelector((state: any) => state.appData.COEUsers);
  console.log("COEUsers from Redux:", COEUsers);

  // Add state to manage which overlay is active to prevent stacking
  const [activeOverlay, setActiveOverlay] = React.useState<
    "none" | "review" | "add"
  >("none");

  // Memoize the combined users list to prevent performance issues
  const combinedUsers = React.useMemo(() => {
    const allUsers = [...(FieldUsers || []), ...(COEUsers || [])];
    const uniqueUsers = new Map(
      allUsers.map((user) => [
        user.UserEmail,
        {
          id: user.UserEmail,
          displayText: user.UserEmail,
        },
      ])
    );
    return Array.from(uniqueUsers.values());
  }, [FieldUsers, COEUsers]);

  // Wrapper functions to manage overlay transitions and prevent stacking
  const handleOpenReviewOverlayWrapper = () => {
    setActiveOverlay("review");
    handleOpenReviewOverlay();
    setShowReviewValidation(false);
  };

  const handleCloseReviewOverlayWrapper = () => {
    setActiveOverlay("none");
    handleCloseReviewOverlay();
    setShowReviewValidation(false);
  };

  const handleOpenAddOverlayFromReview = () => {
    // Close review overlay first, then open add overlay
    setActiveOverlay("add");
    handleCloseReviewOverlay();
    setTimeout(() => {
      handleOpenAddOverlay();
    }, 100); // Small delay to ensure smooth transition
  };

  const handleCloseAddOverlayWrapper = () => {
    setActiveOverlay("none");
    handleCloseAddOverlay();
  };

  const handleBackToReviewFromAdd = () => {
    // Close add overlay and return to review overlay
    setActiveOverlay("review");
    handleCloseAddOverlay();
    setTimeout(() => {
      handleOpenReviewOverlay();
    }, 100);
  };

  // const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  console.log("Created By value from redux", modifiedBy);
  console.log("Selected row data status:", selectedRowData?.status);
  console.log("Is status completed:", isStatusCompleted);
  console.log("Is from begin button:", isFromBeginButton);
  console.log(
    "Current observation reviewed:",
    reviewedObservations.has(currentObservationIndex)
  );

  const handleAddRandomObservationWrapper = () => {
    const selectedDatesPayload = selectedSubmissionDates
      .map((date) => date.displayText)
      .join(", ");

    console.log("Selected Dates Payload:", selectedDatesPayload);

    // Pass the selected dates payload to the existing function or API call
    handleAddRandomObservation(); // Ensure the function uses the selectedDatesPayload internally
  };

  // Add a state to display the toast message directly in the UI
  const [toastMessage, setToastMessage] = React.useState<string | null>(null);

  // Show toast/alert on successful completion
  const showSuccessToast = (message: string) => {
    console.log("Displaying toast/alert:", message); // Debugging log
    setToastMessage(message); // Set the message to be displayed in the UI
    Alert.alert(
      "Success",
      message,
      [{ text: "OK", onPress: () => console.log("Alert dismissed") }],
      { cancelable: true }
    );
  };

  // Add a styled container for success messages
  const renderSuccessMessage = () =>
    toastMessage && (
      <View style={[
        styles.successMessageContainer,
        isDark && { backgroundColor: '#15301A', borderColor: '#1F4D2A' },
      ]}>
        <Text style={[
          styles.successMessageText,
          isDark && { color: '#8FEBA8' },
        ]}>{toastMessage}</Text>
      </View>
    );

  // Scroll-to-top functionality
  const handleScroll = (event: any) => {
    const scrollY = event.nativeEvent.contentOffset.y;
    const shouldShow = scrollY > 100; // Show button after scrolling 100px

    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const scrollToTop = () => {
    scrollViewRef.current?.scrollTo({
      y: 0,
      animated: true,
    });
  };

  // Update reviewed set when submitting review
  // Accepts actionType: 'reviewed' (for Complete) or 'save' (for Save for later)
  const handleSubmitReviewWrapper = async (actionType: "reviewed" | "save") => {
    console.log("Submitting review...");

    // Set submission flag to prevent data loading interference
    setIsSubmittingWrapper(true);
    setHasRecentSubmission(true); // Mark that we're about to submit

    try {
      // Capture the current observation ID at the time of submission
      const submissionObservationId =
        randomObservations.length > 0 &&
        currentObservationIndex < randomObservations.length
          ? randomObservations[
              currentObservationIndex
            ]?.observation_id?.toString()
          : null;

      // Only validate for 'reviewed' (Complete) action, not for 'save' (Save for later)
      if (actionType === "reviewed") {
        setShowReviewValidation(true);

        // Check if required fields are filled
        if (!reviewType || !reviewStatus || reviewStatus.length === 0) {
          console.log("Validation failed: missing required fields");
          return; // Don't proceed if validation fails
        }
      }

      // Clear validation state if validation passes or if saving for later
      setShowReviewValidation(false);

      // Store review data in Redux before submitting to API
      // Only store meaningful review data when a review audit is actually completed
      if (submissionObservationId) {
        const hasData =
          reviewType ||
          (reviewStatus && reviewStatus.length > 0) ||
          reviewComment;
        console.log(
          "Storing review data in Redux for observation:",
          submissionObservationId,
          {
            reviewType,
            reviewStatus,
            reviewComment,
            hasData,
          }
        );

        // Only store data if it's a "reviewed" action (actual review audit submission)
        // For "save" action without meaningful data, don't store anything to avoid confusion
        if (actionType === "reviewed") {
          // Always store the actual form data for completed reviews (even if empty, it represents the completed review)
          dispatch(
            setReviewAuditData({
              observationId: submissionObservationId,
              reviewType,
              reviewStatus,
              reviewComment,
            })
          );

          // Mark this observation as having completed review
          setCompletedReviewObservations((prev) =>
            new Set(prev).add(submissionObservationId)
          );
        } else if (actionType === "save" && hasData) {
          // Only store for "save" action if there's meaningful data (user is in the middle of a review)
          dispatch(
            setReviewAuditData({
              observationId: submissionObservationId,
              reviewType,
              reviewStatus,
              reviewComment,
            })
          );
        }
        // If actionType === 'save' and hasData === false, don't store anything
        // This allows the API call to use default values
      }

      const status = actionType === "reviewed" ? "Completed" : "In Progress";

      // Set flag to prevent form clearing after successful submission
      setHasRecentSubmission(true);

      await handleSubmitReview(status, {
        reviewType,
        reviewStatus,
        reviewComment,
      }); // Pass form data along with status
      console.log("Review submitted successfully");
      setReviewedObservations((prev) =>
        new Set(prev).add(currentObservationIndex)
      );
      setIsReviewAuditComplete(true);
      
      // Get observation details for success message
      const currentObs = randomObservations[currentObservationIndex];
      const locationName = currentObs?.site || currentObs?.siteRegion || selectedLocation?.displayText || "Unknown Location";
      const submitterName = modifiedBy || createdBy || auditFilters.email || userEmail || "Unknown Submitter";
      
      setToastMessage(
        `Review submitted successfully for ${locationName}, submitted by ${submitterName}`
      ); // Mark current observation as reviewed if it's a complete action
      if (actionType === "reviewed") {
        setCurrentObservationReviewed(true);
        setCompletedObservations((prev) => {
          const updated = new Set(prev);
          updated.add(currentObservationIndex);
          return updated;
        });

        // Clear form data immediately after successful review submission
        // to prevent data from carrying over to next observation
        setReviewType(null);
        setReviewStatus([]);
        setReviewComment("");
        console.log(
          "Review audit completed - form data cleared to prevent carryover"
        );

        // DON'T clear stored review data from Redux when review is completed
        // Keep it so that "Save for Later" can use the submitted review data
        // if (submissionObservationId) {
        //   dispatch(clearReviewAuditData(submissionObservationId));
        // }
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      // Don't set reviewed state if submission failed
      setToastMessage("Failed to submit review. Please try again.");
    } finally {
      // Clear the submission flags after a delay to allow UI updates
      setTimeout(() => {
        setIsSubmittingWrapper(false);
        setHasRecentSubmission(false); // Clear the recent submission flag
      }, 1000); // Increased delay to ensure no interference
    }

    // If Save for later, refresh the main table data by calling the parent callback
    if (actionType === "save" && typeof onRedirectComplete === "function") {
      await onRedirectComplete();
    }
  };

  React.useEffect(() => {
    const fetchSubmitterDropdownValues = async () => {
      try {
        const api = createApiClient();
        const response = await apiClient(
          api,
          `/common/users/users-by-role-ids?roleIds=1,2,3,4,5,6,7,8,9&location=${selectedLocation?.displayText}`,
          "GET"
        );
        console.log("API response for users-by-role-ids:", response);
        let users = [];
        if (Array.isArray(response?.users)) {
          users = response.users;
        } else if (Array.isArray(response?.data)) {
          users = response.data;
        } else if (Array.isArray(response?.data?.users)) {
          users = response.data.users;
        } else if (Array.isArray(response)) {
          users = response;
        } else {
          users = [];
        }
        const dropdownValues =
          users.length > 0
            ? users.map((user: any) => ({
                id:
                  user.UserEmail ||
                  user.email ||
                  user.userPrincipalName ||
                  user.GPID ||
                  user.id,
                displayText:
                  user.UserEmail ||
                  user.email ||
                  user.userPrincipalName ||
                  user.GPID ||
                  user.id, // Removed appended location
              }))
            : [{ id: "none", displayText: "No users found" }];
        setAuditFilters((prevFilters: any) => ({
          ...prevFilters,
          submiiterOptions: dropdownValues,
        }));
      } catch (error) {
        console.error("Error fetching submitter dropdown values:", error);
        // Add centralized error and show modal overlay
        try {
          handleQualityAuditErrorWithFriendlyMessage(
            'fetchUsersByRole',
            error,
            true,
            { location: selectedLocation?.displayText }
          );
        } catch (handlerErr) {
          console.error('Error while dispatching users-by-role error:', handlerErr);
        }
        setAuditFilters((prevFilters: any) => ({
          ...prevFilters,
          submiiterOptions: [
            { id: "none", displayText: "Error loading users" },
          ],
        }));
      }
    };

    fetchSubmitterDropdownValues();
  }, []);

  React.useEffect(() => {
    // Fetch LCW dates for the date dropdown
    const fetchLcwDates = async () => {
      try {
        const api = createApiClient();
        const response = await apiClient(api, "/common/lcw_dates", "GET");
        let dateOptions = [];
        if (Array.isArray(response?.dates)) {
          dateOptions = response.dates.map((date: string) => ({
            id: date,
            displayText: date.slice(0, 10), // Format as YYYY-MM-DD
          }));
        }
        dateOptions.push({ id: "all", displayText: "All" });
        setSubmissionDateOptions(dateOptions);
      } catch (error) {
        console.error("Error fetching LCW dates:", error);
        setSubmissionDateOptions([
          { id: "none", displayText: "Error loading dates" },
        ]);
      }
    };
    fetchLcwDates();
  }, []);

  React.useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Reset current observation reviewed state when observation index changes
  React.useEffect(() => {
    console.log(`Observation index changed to: ${currentObservationIndex}`);
    setCurrentObservationReviewed(false);
    // Clear recent submission flag when observation changes to allow form loading
    setHasRecentSubmission(false);

    // Note: Form clearing is now handled by the parent component (QualityAudit.tsx)
    // to avoid timing conflicts between multiple useEffect hooks
  }, [currentObservationIndex]);

  // Track if we're in the middle of a submission to prevent interference
  const [isSubmittingWrapper, setIsSubmittingWrapper] = React.useState(false);

  // Track successful submissions to prevent clearing form data
  const [hasRecentSubmission, setHasRecentSubmission] = React.useState(false);

  // Track which observations have had their review completed to prevent overwriting with defaults
  const [completedReviewObservations, setCompletedReviewObservations] =
    React.useState<Set<string>>(new Set());

  // Load stored review data when observation changes
  React.useEffect(() => {
    // Don't load stored data automatically when navigating between observations
    // This prevents carrying over form data from previous observations
    // Data will only be loaded when user actively opens the Review Audit overlay
    console.log(
      "Observation changed - NOT auto-loading stored data to prevent data carryover"
    );
  }, [currentObservationId, storedReviewData]);

  // Show a toast on the main screen only after the Add overlay is closed and there was an addOverlayError
  const prevActiveOverlayRef = React.useRef(activeOverlay);
  React.useEffect(() => {
    // If overlay just closed and there was an error, show toast
    if (
      prevActiveOverlayRef.current === "add" &&
      activeOverlay === "none" &&
      addOverlayError
    ) {
      setToastMessage(addOverlayError);
    }
    prevActiveOverlayRef.current = activeOverlay;
  }, [activeOverlay, addOverlayError]);

  const [selectedSubmissionDates, setSelectedSubmissionDates] = React.useState<
    any[]
  >([]);

  const handleDateSelection = (selected: any[]) => {
    const selectedDates = selected.map((item) => item.displayText);
    dispatch(setSelectedDates(selectedDates)); // Dispatch selected dates to Redux
    console.log("Selected Dates:", selectedDates);
  };

  // Export to PDF handler

  // PDF Export State
  const [isPdfExporting, setIsPdfExporting] = React.useState(false);
  const toast = useToast();

  if (
    randomObservations.length > 0 &&
    currentObservationIndex < randomObservations.length
  ) {
    // Debug logging for Export to PDF button conditions
    console.log("Export to PDF button conditions:", {
      isStatusCompleted,
      isPdfExporting,
      isFromBeginButton,
      hasCurrentObservationReviewed: reviewedObservations.has(
        currentObservationIndex
      ),
      shouldDisable:
        !isStatusCompleted ||
        isPdfExporting ||
        (isFromBeginButton &&
          !reviewedObservations.has(currentObservationIndex)),
    });

    // PDF Export Handler for QualityAudit (must be inside render to access obs/obsId)
    const handlePdfExport = async () => {
      if (!obsId || !userEmail) {
        toast.showToast({
          header: "PDF Export Error",
          subtext: "Audit must be saved before exporting PDF.",
          variant: "error",
        });
        return;
      }
      try {
        setIsPdfExporting(true);
        // Build the subject line
        console.log("Observation Details new", obs.id);
        const recordName = obs?.name || obs?.observation_type || "Unknown";
        const subjectLine = `Quality Audit Report for ${recordName} - ${obsId}`;
        // Build the API URL with query parameters
        const api = createApiClient();
        const response = await apiClient(
          api,
          `/notifications/email/pdf?emailTo=${encodeURIComponent(
            userEmail
          )}&inspection_module=quality_audit&id=${
            obs.id
          }&subject=${encodeURIComponent(subjectLine)}`,
          "GET"
        );
        // Handle the response
        if (response && response.message) {
          toast.showToast({
            header: "Email Notification",
            subtext: response.message,
            variant: response.messageId ? "success" : "error",
          });
          console.log("PDF export success:", response.message);
        } else {
          toast.showToast({
            header: "Email Error",
            subtext: "Failed to send PDF export email",
            variant: "error",
          });
          throw new Error("Invalid response from server");
        }
        console.log("API response:", response);
      } catch (error: any) {
        console.error("PDF Export Error:", error);
        toast.showToast({
          header: "Email Error",
          subtext: error?.message || "Failed to export PDF",
          variant: "error",
        });
      } finally {
        setIsPdfExporting(false);
      }
    };
    const obs = randomObservations[currentObservationIndex];
    console.log("Current Observation:", obs);
    console.log("Observation Details:", randomObservations);
    const obsType = obs.observation_type;
    const obsId = obs.observation_id;
    if (isRandomObsLoading) {
      return (
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <Text>Loading observation...</Text>
        </View>
      );
    }
    if (randomObsError) {
      return (
        <View
          style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        >
          <Text style={{ color: "red" }}>{randomObsError}</Text>
        </View>
      );
    }
    let SubmittedScreen = null;
    let submittedScreenProps = {};
    if (
      obsType === "Pulse Check" ||
      obsType === "Routine" ||
      obsType === "Full"
    ) {
      SubmittedScreen = require("../screens/PulseCheck/index").default;
      submittedScreenProps = {
        readOnly: true,
        onBackPress: () => {
          setRandomObservations([]);
          setCurrentObservationIndex(0);
          setObservationDetails(null);
          if (onRedirectComplete) onRedirectComplete();
        },
        forceDisabled: true,
        disabled: true,
      };
    } else if (obsType === "Shift Handover") {
      SubmittedScreen = require("./ShiftHandOver/ShiftHandOverDetails").default;
      submittedScreenProps = {
        isVisible: true,
        onBackPress: () => {
          setRandomObservations([]);
          setCurrentObservationIndex(0);
          setObservationDetails(null);
          if (onRedirectComplete) onRedirectComplete();
        },
        forceDisabled: true,
      };
    } else if (obsType === "SPARC Walk") {
      if (sparcWalkReady && !showSparcWalkLoader) {
        SubmittedScreen =
          require("./sparcwalk/SparcWalkInspectionScreen").default;
        submittedScreenProps = {
          selectedZone: {
            id: currentSparcWalkObs?.zone_id || "1",
            displayText: currentSparcWalkObs?.zone || "Zone 1",
          },
          selectedCategory: {
            id: currentSparcWalkObs?.category_id || "1",
            displayText: currentSparcWalkObs?.category || "Safety",
          },
          onBackPress: () => {
            setRandomObservations([]);
            setCurrentObservationIndex(0);
            setObservationDetails(null);
            setShowSparcWalkLoader(false);
            setSparcWalkReady(false);
            setCurrentSparcWalkObs(null);
            if (onRedirectComplete) onRedirectComplete();
          },
          isDisabled: true,
          selectedWalkData: currentSparcWalkObs,
        };
      } else {
        SubmittedScreen =
          require("../components/qualityAudit/qualityAuditSparcWalk").default;
        submittedScreenProps = {
          observationId: obsId,
          observation: currentSparcWalkObs,
          onBackPress: () => {
            setRandomObservations([]);
            setCurrentObservationIndex(0);
            setObservationDetails(null);
            setShowSparcWalkLoader(false);
            setSparcWalkReady(false);
            setCurrentSparcWalkObs(null);
            if (onRedirectComplete) onRedirectComplete();
          },
          onComplete: () => {
            setShowSparcWalkLoader(false);
            setSparcWalkReady(true);
          },
          setCurrentSparcWalkObs: setCurrentSparcWalkObs,
        };
      }
    } else if (obsType === "Leadership Walk") {
      SubmittedScreen = require("./LeaderShipWalkRedirectScreen").default;
      submittedScreenProps = {
        observationId: obsId,
        readOnly: true,
        details: observationDetails?.details,
        forceDisabled: true,
        onBackPress: () => {
          // Reset Leadership Walk Redux state
          try {
            const {
              resetState: resetLeadershipWalkState,
            } = require("../store/slices/leaderShipWalkSlice");
            const {
              resetState: resetRedirectState,
            } = require("../store/slices/leadershipWalkRedirectSlice");
            dispatch(resetLeadershipWalkState());
            dispatch(resetRedirectState());
          } catch (e) {
            console.log("Error resetting Leadership Walk state:", e);
          }

          // Reset main Quality Audit state
          setRandomObservations([]);
          setCurrentObservationIndex(0);
          setObservationDetails(null);
          if (onRedirectComplete) onRedirectComplete();
        },
      };
    }
    
    // Check if device is strictly mobile (iPhone or Android phone, not iPad)
    const { width } = Dimensions.get("window");
  const isMobilePhone = (Platform.OS === 'ios' || Platform.OS === 'android') && width < 768;
  // Button font size: 24px on web, 16px on iOS (iPhone and iPad) and other native platforms
  const buttonFontSize = Platform.OS === 'web' ? 16 : 14;
    const isOverlayActive = activeOverlay !== "none";

    // Dark-mode button color helpers (mobile/tablet only)
    const btnDisabledBg = isDark ? '#3A3A3A' : '#DADADA';
    const btnDisabledBorder = isDark ? '#3A3A3A' : '#DADADA';
    const btnDisabledText = isDark ? '#9CA3AF' : '#fff';
    const btnSecondaryEnabledBg = isDark ? '#000000' : '#FFF';
    const btnSecondaryEnabledBorder = isDark ? '#005CBC' : '#034895';
    const btnSecondaryEnabledText = isDark ? '#FFFFFF' : '#034895';

    // For mobile phones only: if overlay is active, show only the ReviewAddModal in full-screen
    if (isMobilePhone && isOverlayActive) {
      return (
        <View style={{ flex: 1 }}>
          <ReviewAddModal
            activeOverlay={activeOverlay}
            setActiveOverlay={setActiveOverlay}
            reviewType={reviewType}
            setReviewType={setReviewType}
            reviewStatus={reviewStatus}
            setReviewStatus={setReviewStatus}
            reviewComment={reviewComment}
            setReviewComment={setReviewComment}
            showReviewValidation={showReviewValidation}
            setShowReviewValidation={setShowReviewValidation}
            handleCloseReviewOverlayWrapper={handleCloseReviewOverlayWrapper}
            handleSubmitReviewWrapper={handleSubmitReviewWrapper}
            handleOpenAddOverlay={handleOpenAddOverlay}
            handleCloseAddOverlayWrapper={handleCloseAddOverlayWrapper}
            handleOpenAddOverlayFromReview={handleOpenAddOverlayFromReview}
            handleAddRandomObservationWrapper={handleAddRandomObservationWrapper}
            handleDateSelection={handleDateSelection}
            isSubmittingReview={isSubmittingReview}
            buttonsEnabled={buttonsEnabled}
            addButtonEnabled={addButtonEnabled}
            currentObservationIndex={currentObservationIndex}
            reviewedObservations={reviewedObservations}
            currentObservationId={currentObservationId}
            toastMessage={toastMessage}
            addOverlayError={addOverlayError}
            combinedUsers={combinedUsers}
            modifiedBy={modifiedBy}
            createdBy={createdBy}
            FieldUsers={FieldUsers}
            COEUsers={COEUsers}
            userEmail={userEmail}
            auditFilters={auditFilters}
            setAuditFilters={setAuditFilters}
            submissionTypeOptions={submissionTypeOptions}
            setSubmissionTypeOptions={setSubmissionTypeOptions}
            selectedSubmissionDates={selectedSubmissionDates}
            setSelectedSubmissionDates={setSelectedSubmissionDates}
            submissionDateOptions={submissionDateOptions}
          />
        </View>
      );
    }

    // Render for both iOS and web/other platforms (tablet and desktop)
    return (
      <View style={{ flex: 1, backgroundColor: isDark ? '#141414' : undefined }}>
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 16,
            backgroundColor: isDark ? '#1A1A1A' : undefined,
          }}
        >
          <Text style={{ fontWeight: "bold", color: isDark ? '#F2F2F2' : undefined }}>
            Submission {currentObservationIndex + 1} of {randomObservations.length}
          </Text>
          <TouchableOpacity
            onPress={() => {
              setRandomObservations([]);
              setCurrentObservationIndex(0);
              if (typeof dispatch === "function") {
                try {
                  dispatch(
                    require("../store/slices/qualityAuditSlice").resetSelectedRowData()
                  );
                } catch (e) {}
              }
              if (onRedirectComplete) onRedirectComplete();
            }}
          >
            {/* <Text style={{ color: '#0066cc' }}>Back</Text>  */}
          </TouchableOpacity>
        </View>
        <View style={{ flex: 1 }}>
          {/* Main content: ScrollView for iOS, View for web/other */}
          {Platform.OS === "ios" ? (
            <View style={{ flex: 1 }}>
              <ScrollView 
                ref={scrollViewRef}
                style={{ flex: 1 }}
                contentContainerStyle={{ flexGrow: 1 }}
                onScroll={handleScroll}
                scrollEventThrottle={16}
              >
              {SubmittedScreen ? (
                <SubmittedScreen {...submittedScreenProps} />
              ) : (
                <Text>Unsupported observation type: {obsType}</Text>
              )}
              {/* Button group as a column for iOS */}
              <View style={{ padding: 16, backgroundColor: isDark ? '#141414' : undefined }}>
                <View style={{ flexDirection: "column", gap: 12 }}>
                  <LhButton
                    text={isPdfExporting ? "Exporting..." : "Export to PDF"}
                    variant="secondary"
                    size="medium"
                    onPress={handlePdfExport}
                    disabled={
                      !isStatusCompleted ||
                      isPdfExporting ||
                      (isFromBeginButton &&
                        !reviewedObservations.has(currentObservationIndex))
                    }
                    style={{
                      backgroundColor:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? btnDisabledBg
                          : btnSecondaryEnabledBg,
                      opacity:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? 0.7
                          : 1,
                      borderWidth: 1,
                      borderColor:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? btnDisabledBorder
                          : btnSecondaryEnabledBorder,
                      minWidth: 250,
                      width: "100%",
                      alignSelf: "stretch",
                      marginBottom: 12,
                      padding:2,
                    }}
                    textStyle={{
                      color:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? btnDisabledText
                          : btnSecondaryEnabledText,
                      paddingHorizontal: 4,
                      fontSize: buttonFontSize,
                    }}
                  />
                  <LhButton
                    text="Previous"
                    variant="secondary"
                    size="medium"
                    onPress={() => {
                      if (currentObservationIndex > 0) {
                        // Clear all review audit data from Redux before navigating to previous observation
                        dispatch(resetAllReviewAuditData());
                        console.log(
                          "Previous button (iOS): Cleared all Redux review audit data to prevent carryover"
                        );
                        setCurrentObservationIndex(currentObservationIndex - 1);
                      }
                    }}
                    disabled={currentObservationIndex === 0}
                    style={{
                      backgroundColor:
                        currentObservationIndex === 0 ? btnDisabledBg : btnSecondaryEnabledBg,
                      opacity: currentObservationIndex === 0 ? 0.7 : 1,
                      borderWidth: 1,
                      borderColor:
                        currentObservationIndex === 0
                          ? btnDisabledBorder
                          : btnSecondaryEnabledBorder,
                      minWidth: 250,
                      width: "100%",
                      alignSelf: "stretch",
                      marginBottom: 12,
                       padding:2,
                    }}
                    textStyle={{
                      color: currentObservationIndex === 0 ? btnDisabledText : btnSecondaryEnabledText,
                      paddingHorizontal: 4,
                      fontSize: buttonFontSize,
                    }}
                  />
                  <LhButton
                    text="Save for later"
                    variant="secondary"
                    size="medium"
                    onPress={async () => {
                      console.log("=== SAVE FOR LATER CLICKED ===");
                      console.log("Current form state before save:", {
                        reviewType,
                        reviewStatus,
                        reviewComment,
                      });
                      console.log(
                        "Current observation ID:",
                        currentObservationId
                      );
                      console.log(
                        "Stored review data before save:",
                        storedReviewData
                      );
                      console.log(
                        "Completed review observations:",
                        completedReviewObservations
                      );
                      console.log(
                        "Has completed review for this observation:",
                        completedReviewObservations.has(
                          currentObservationId || ""
                        )
                      );
                      await handleSubmitReviewWrapper("save");
                      // Clear all review audit data when leaving the observation flow
                      dispatch(resetAllReviewAuditData());
                      console.log(
                        "Save for Later (iOS): Cleared all Redux review audit data when leaving observation flow"
                      );
                      setRandomObservations([]);
                      setCurrentObservationIndex(0);
                      setObservationDetails(null);
                    }}
                    disabled={
                      !buttonsEnabled ||
                      completedReviewObservations.has(currentObservationId || "")
                    }
                    style={{
                      backgroundColor:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? btnSecondaryEnabledBg
                          : btnDisabledBg,
                      borderWidth: 1,
                      borderColor:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? btnSecondaryEnabledBorder
                          : btnDisabledBorder,
                      paddingHorizontal: 20,
                      paddingVertical: 8,
                      minWidth: 100,
                      alignSelf: "stretch",
                      marginBottom: 12,
                      opacity:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? 1
                          : 0.6,
                    }}
                    textStyle={{
                      color:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? btnSecondaryEnabledText
                          : btnDisabledText,
                      paddingHorizontal: 4,
                      fontSize: buttonFontSize,
                    }}
                  />
                  <LhButton
                    text="Review Audit"
                    variant="secondary"
                    size="medium"
                    onPress={() => {
                      // Do NOT load stored review data automatically when opening overlay
                      // This prevents data from previous observations from appearing
                      console.log(
                        "Opening Review Audit overlay - NOT loading stored data to prevent cross-observation interference"
                      );

                      handleOpenReviewOverlayWrapper();
                    }}
                    disabled={!buttonsEnabled}
                    style={{
                      backgroundColor: buttonsEnabled ? "#0066CC" : btnDisabledBg,
                      borderWidth: 0,
                      paddingHorizontal: 20,
                      paddingVertical: 8,
                      minWidth: 120,
                      alignSelf: "stretch",
                      marginBottom: 12,
                      opacity: buttonsEnabled ? 1 : 0.6,
                    }}
                    textStyle={{ color: "white", paddingHorizontal: 4, fontSize: buttonFontSize }}
                  />
                  <LhButton
                    text={
                      currentObservationIndex < randomObservations.length - 1
                        ? "Next"
                        : "Complete"
                    }
                    variant="primary"
                    size="medium"
                    onPress={() => {
                      if (
                        currentObservationIndex <
                        randomObservations.length - 1
                      ) {
                        // Clear all review audit data from Redux before navigating to next observation
                        dispatch(resetAllReviewAuditData());
                        console.log(
                          "Next button (iOS): Cleared all Redux review audit data to prevent carryover"
                        );
                        setCurrentObservationIndex((i: number) => i + 1);
                      } else {
                        // Clear all review audit data when completing all observations
                        dispatch(resetAllReviewAuditData());
                        console.log(
                          "Complete button (iOS): Cleared all Redux review audit data"
                        );
                        setRandomObservations([]);
                        setCurrentObservationIndex(0);
                        setObservationDetails(null);
                        if (onRedirectComplete) onRedirectComplete();
                      }
                    }}
                    disabled={
                      isStatusCompleted
                        ? false
                        : isFromBeginButton
                        ? !reviewedObservations.has(currentObservationIndex)
                        : !reviewedObservations.has(currentObservationIndex)
                    }
                    style={{
                      backgroundColor: isStatusCompleted
                        ? currentObservationIndex <
                          randomObservations.length - 1
                          ? "#005cbc"
                          : "#008242"
                        : isFromBeginButton
                        ? !reviewedObservations.has(currentObservationIndex)
                          ? btnDisabledBg
                          : currentObservationIndex <
                            randomObservations.length - 1
                          ? "#005cbc"
                          : "#008242"
                        : !reviewedObservations.has(currentObservationIndex)
                        ? btnDisabledBg
                        : currentObservationIndex <
                          randomObservations.length - 1
                        ? "#005cbc"
                        : "#008242",
                      opacity: isStatusCompleted
                        ? 1
                        : isFromBeginButton
                        ? !reviewedObservations.has(currentObservationIndex)
                          ? 0.7
                          : 1
                        : !reviewedObservations.has(currentObservationIndex)
                        ? 0.7
                        : 1,
                      borderWidth: 0,
                      paddingHorizontal: 20,
                      paddingVertical: 8,
                      minWidth: 100,
                      alignSelf: "stretch",
                      marginBottom: 0,
                    }}
                    textStyle={{ color: "white", paddingHorizontal: 4, fontSize: buttonFontSize }}
                  />
                </View>
              </View>
            </ScrollView>
            
            {/* Scroll to Top Button for iOS */}
            {showScrollToTop && (
              <Animated.View
                style={[
                  styles.scrollToTopButton,
                  {
                    opacity: scrollButtonOpacity,
                    transform: [
                      {
                        scale: scrollButtonOpacity.interpolate({
                          inputRange: [0, 1],
                          outputRange: [0.8, 1],
                        }),
                      },
                    ],
                  },
                ]}
                pointerEvents="auto"
              >
                <TouchableOpacity
                  style={[
                    styles.scrollToTopTouchable,
                    isDark && { backgroundColor: '#2B2B2B', borderColor: '#404040' },
                  ]}
                  onPress={scrollToTop}
                  accessibilityLabel="Scroll to top"
                  accessibilityRole="button"
                >
                  <Animated.View style={styles.arrowIcon}>
                    <Text style={styles.arrowText}>↑</Text>
                  </Animated.View>
                </TouchableOpacity>
              </Animated.View>
            )}
            </View>
          ) : (
            <View style={{ flex: 1 }}>
              {SubmittedScreen ? (
                <SubmittedScreen {...submittedScreenProps} />
              ) : (
                <Text>Unsupported observation type: {obsType}</Text>
              )}
              {/* Button group as a single row for web/other platforms (no wrap, with horizontal padding) */}
              <View style={{ paddingVertical: 16, paddingHorizontal: 56 }}>
                <View
                  style={{
                    flexDirection: "row",
                    gap: 12,
                    flexWrap: "nowrap",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <LhButton
                    text={isPdfExporting ? "Exporting..." : "Export to PDF"}
                    variant="secondary"
                    size="medium"
                    onPress={handlePdfExport}
                    disabled={
                      !isStatusCompleted ||
                      isPdfExporting ||
                      (isFromBeginButton &&
                        !reviewedObservations.has(currentObservationIndex))
                    }
                    style={{
                      backgroundColor:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? btnDisabledBg
                          : btnSecondaryEnabledBg,
                      opacity:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? 0.7
                          : 1,
                      borderWidth: 1,
                      borderColor:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? btnDisabledBorder
                          : btnSecondaryEnabledBorder,
                      minWidth: 100,
                      marginRight: 0,
                    }}
                    textStyle={{
                      color:
                        !isStatusCompleted ||
                        isPdfExporting ||
                        (isFromBeginButton &&
                          !reviewedObservations.has(currentObservationIndex))
                          ? btnDisabledText
                          : btnSecondaryEnabledText,
                      paddingHorizontal: 4,
                      fontSize: buttonFontSize,
                    }}
                  />
                  <LhButton
                    text="Previous"
                    variant="secondary"
                    size="medium"
                    onPress={() => {
                      if (currentObservationIndex > 0) {
                        // Clear all review audit data from Redux before navigating to previous observation
                        dispatch(resetAllReviewAuditData());
                        console.log(
                          "Previous button (web/other): Cleared all Redux review audit data to prevent carryover"
                        );
                        setCurrentObservationIndex(currentObservationIndex - 1);
                      }
                    }}
                    disabled={currentObservationIndex === 0}
                    style={{
                      backgroundColor:
                        currentObservationIndex === 0 ? btnDisabledBg : btnSecondaryEnabledBg,
                      opacity: currentObservationIndex === 0 ? 0.7 : 1,
                      borderWidth: 1,
                      borderColor:
                        currentObservationIndex === 0
                          ? btnDisabledBorder
                          : btnSecondaryEnabledBorder,
                      minWidth: 150,
                      marginRight: 0,
                    }}
                    textStyle={{
                      color: currentObservationIndex === 0 ? btnDisabledText : btnSecondaryEnabledText,
                      paddingHorizontal: 4,
                      fontSize: buttonFontSize,
                    }}
                  />
                  <LhButton
                    text="Save for later"
                    variant="secondary"
                    size="medium"
                    onPress={async () => {
                      await handleSubmitReviewWrapper("save");
                      // Clear all review audit data when leaving the observation flow
                      dispatch(resetAllReviewAuditData());
                      console.log(
                        "Save for Later (web/other): Cleared all Redux review audit data when leaving observation flow"
                      );
                      setRandomObservations([]);
                      setCurrentObservationIndex(0);
                      setObservationDetails(null);
                    }}
                    disabled={
                      !buttonsEnabled ||
                      completedReviewObservations.has(currentObservationId || "")
                    }
                    style={{
                      backgroundColor:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? btnSecondaryEnabledBg
                          : btnDisabledBg,
                      borderWidth: 1,
                      borderColor:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? btnSecondaryEnabledBorder
                          : btnDisabledBorder,
                      paddingHorizontal: 20,
                      paddingVertical: 8,
                      minWidth: 120,
                      marginRight: 0,
                      opacity:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? 1
                          : 0.6,
                    }}
                    textStyle={{
                      color:
                        buttonsEnabled &&
                        !completedReviewObservations.has(currentObservationId || "")
                          ? btnSecondaryEnabledText
                          : btnDisabledText,
                      paddingHorizontal: 4,
                      fontSize: buttonFontSize,
                    }}
                  />
                  <LhButton
                    text="Review Audit"
                    variant="secondary"
                    size="medium"
                    onPress={() => {
                      // Do NOT load stored review data automatically when opening overlay
                      // This prevents data from previous observations from appearing
                      console.log(
                        "Opening Review Audit overlay - NOT loading stored data to prevent cross-observation interference"
                      );

                      handleOpenReviewOverlayWrapper();
                    }}
                    disabled={!buttonsEnabled}
                    style={{
                      backgroundColor: buttonsEnabled ? "#0066CC" : btnDisabledBg,
                      borderWidth: 0,
                      paddingHorizontal: 20,
                      paddingVertical: 8,
                      minWidth: 120,
                      marginRight: 0,
                      opacity: buttonsEnabled ? 1 : 0.6,
                    }}
                    textStyle={{ color: "white", paddingHorizontal: 4 }}
                    
                  />
                  <LhButton
                    text={
                      currentObservationIndex < randomObservations.length - 1
                        ? "Next"
                        : "Complete"
                    }
                    variant="primary"
                    size="medium"
                    onPress={() => {
                      if (
                        currentObservationIndex <
                        randomObservations.length - 1
                      ) {
                        // Clear all review audit data from Redux before navigating to next observation
                        dispatch(resetAllReviewAuditData());
                        console.log(
                          "Next button (web/other): Cleared all Redux review audit data to prevent carryover"
                        );
                        setCurrentObservationIndex((i: number) => i + 1);
                      } else {
                        // Clear all review audit data when completing all observations
                        dispatch(resetAllReviewAuditData());
                        console.log(
                          "Complete button (web/other): Cleared all Redux review audit data"
                        );
                        setRandomObservations([]);
                        setCurrentObservationIndex(0);
                        setObservationDetails(null);
                        if (onRedirectComplete) onRedirectComplete();
                      }
                    }}
                    disabled={
                      isStatusCompleted
                        ? false
                        : isFromBeginButton
                        ? !reviewedObservations.has(currentObservationIndex)
                        : !reviewedObservations.has(currentObservationIndex)
                    }
                    style={{
                      backgroundColor: isStatusCompleted
                        ? currentObservationIndex <
                          randomObservations.length - 1
                          ? "#005cbc"
                          : "#008242"
                        : isFromBeginButton
                        ? !reviewedObservations.has(currentObservationIndex)
                          ? btnDisabledBg
                          : currentObservationIndex <
                            randomObservations.length - 1
                          ? "#005cbc"
                          : "#008242"
                        : !reviewedObservations.has(currentObservationIndex)
                        ? btnDisabledBg
                        : currentObservationIndex <
                          randomObservations.length - 1
                        ? "#005cbc"
                        : "#008242",
                      opacity: isStatusCompleted
                        ? 1
                        : isFromBeginButton
                        ? !reviewedObservations.has(currentObservationIndex)
                          ? 0.7
                          : 1
                        : !reviewedObservations.has(currentObservationIndex)
                        ? 0.7
                        : 1,
                      borderWidth: 0,
                      paddingHorizontal: 20,
                      paddingVertical: 8,
                      minWidth: 120,
                      marginRight: 0,
                    }}
                    textStyle={{ color: "white", paddingHorizontal: 4 }}
                  />
                </View>
              </View>
            </View>
          )}
        </View>
        {/* Render the review and add overlays using the extracted component for tablets and web */}
        <ReviewAddModal
          activeOverlay={activeOverlay}
          setActiveOverlay={setActiveOverlay}
          reviewType={reviewType}
          setReviewType={setReviewType}
          reviewStatus={reviewStatus}
          setReviewStatus={setReviewStatus}
          reviewComment={reviewComment}
          setReviewComment={setReviewComment}
          showReviewValidation={showReviewValidation}
          setShowReviewValidation={setShowReviewValidation}
          handleCloseReviewOverlayWrapper={handleCloseReviewOverlayWrapper}
          handleSubmitReviewWrapper={handleSubmitReviewWrapper}
          handleOpenAddOverlay={handleOpenAddOverlay}
          handleCloseAddOverlayWrapper={handleCloseAddOverlayWrapper}
          handleOpenAddOverlayFromReview={handleOpenAddOverlayFromReview}
          handleAddRandomObservationWrapper={handleAddRandomObservationWrapper}
          handleDateSelection={handleDateSelection}
          isSubmittingReview={isSubmittingReview}
          buttonsEnabled={buttonsEnabled}
          addButtonEnabled={addButtonEnabled}
          currentObservationIndex={currentObservationIndex}
          reviewedObservations={reviewedObservations}
          currentObservationId={currentObservationId}
          toastMessage={toastMessage}
          addOverlayError={addOverlayError}
          combinedUsers={combinedUsers}
          modifiedBy={modifiedBy}
          createdBy={createdBy}
          FieldUsers={FieldUsers}
          COEUsers={COEUsers}
          userEmail={userEmail}
          auditFilters={auditFilters}
          setAuditFilters={setAuditFilters}
          submissionTypeOptions={submissionTypeOptions}
          setSubmissionTypeOptions={setSubmissionTypeOptions}
          selectedSubmissionDates={selectedSubmissionDates}
          setSelectedSubmissionDates={setSelectedSubmissionDates}
          submissionDateOptions={submissionDateOptions}
        />
      </View>
    );
  }
  return null;
};

const styles = StyleSheet.create({
  successMessageContainer: {
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginTop: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  successMessageText: {
    color: "#155724",
    fontWeight: "700",
    fontSize: 16,
    flex: 1,
  },
  scrollToTopButton: {
    position: "absolute",
    bottom: Platform.OS === "web" ? 30 : 80,
    right: 20,
    zIndex: 1000,
  },
  scrollToTopTouchable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0066CC",
    lineHeight: 24,
  },
});

export default QualityAuditRedirect;
